/**
 * @description Super Admin list column-def contract (Sales Orgs + Merchants)
 *
 * Key features:
 * - One column factory per entity; surfaces pick a subset via config
 * - Sales Orgs: Tenant Name, Owner, Status, Merchants, Created
 *   LynxPay `/tenants` may add Features (optional). Never Hosting.
 * - Merchants: Business, Status, Users, Created
 *   Hosting is optional and LynxPay Sales Org → Merchants only
 *
 * @dependencies
 * - merchantAccountStatus: badge for merchant accountStatus
 *
 * @notes
 * - Presentational defs only. Callers fetch; AdminListTable paints.
 * - Hosting stays off CP → Merchants, under-CP SO → Merchants, and every SO list
 * - Do not add Hosting to `/tenants` or CP → Sales Orgs
 * - Preferred names: channelPartnerId / salesOrgId
 * - Do not flip cpResolution
 */

import {
  merchantAccountStatusBadge,
  resolveMerchantAccountStatus
} from '../../utils/merchantAccountStatus';

export const ADMIN_LIST_CHROME = {
  wrapper: 'bg-white border border-gray-200 rounded-xl overflow-hidden',
  empty: 'bg-white border border-gray-200 rounded-xl p-12 text-center',
  th: 'text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider',
  td: 'px-4 py-3'
};

export const SALES_ORG_COLUMN_KEYS = ['name', 'owner', 'status', 'merchants', 'created'];
export const MERCHANT_COLUMN_KEYS = ['business', 'status', 'users', 'created'];
export const FEATURES_COLUMN_KEY = 'features';
export const HOSTING_COLUMN_KEY = 'hosting';

/**
 * @param {string|null|undefined} value
 * @returns {string}
 */
export function formatAdminListDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
}

/**
 * @param {boolean} isActive
 * @returns {{ label: string, className: string }}
 */
export function salesOrgStatusBadge(isActive) {
  return isActive
    ? { label: 'Active', className: 'bg-green-50 text-green-700' }
    : { label: 'Inactive', className: 'bg-gray-100 text-gray-500' };
}

/**
 * @param {'paid'|'free'|string|null|undefined} tier
 * @returns {string}
 */
export function hostingTierLabel(tier) {
  if (!tier) return '—';
  return tier === 'paid' ? 'Paid' : 'Free';
}

/**
 * @param {{ includeFeatures?: boolean, showContactEmail?: boolean, nameHeader?: string }} [options]
 * @returns {Array<{ key: string, header: string, sortKey?: string, sortable?: boolean, cellClassName?: string, render: Function }>}
 */
export function salesOrgListColumns({
  includeFeatures = false,
  showContactEmail = false,
  nameHeader = 'Tenant Name'
} = {}) {
  const columns = [
    {
      key: 'name',
      header: nameHeader,
      sortKey: 'organizationName',
      sortable: true,
      render: (row) => (
        <>
          <p className="text-sm font-medium text-gray-900">{row.organizationName}</p>
          {showContactEmail ? (
            <p className="text-xs text-gray-400">{row.contactEmail}</p>
          ) : null}
        </>
      )
    },
    {
      key: 'owner',
      header: 'Owner',
      render: (row) => (
        <>
          <p className="text-sm text-gray-700">
            {row.owner?.firstName} {row.owner?.lastName}
          </p>
          <p className="text-xs text-gray-400">{row.owner?.email}</p>
        </>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => {
        const badge = salesOrgStatusBadge(row.isActive);
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
            {badge.label}
          </span>
        );
      }
    }
  ];

  if (includeFeatures) {
    columns.push({
      key: FEATURES_COLUMN_KEY,
      header: 'Features',
      cellClassName: `${ADMIN_LIST_CHROME.td} text-sm text-gray-600`,
      render: (row) => `${row.featureCount?.enabled || 0}/${row.featureCount?.total || 0}`
    });
  }

  columns.push(
    {
      key: 'merchants',
      header: 'Merchants',
      sortKey: 'merchantCount',
      sortable: true,
      cellClassName: `${ADMIN_LIST_CHROME.td} text-sm text-gray-600`,
      render: (row) => row.merchantCount || 0
    },
    {
      key: 'created',
      header: 'Created',
      sortKey: 'createdAt',
      sortable: true,
      cellClassName: `${ADMIN_LIST_CHROME.td} text-sm text-gray-400`,
      render: (row) => formatAdminListDate(row.createdAt)
    }
  );

  return columns;
}

/**
 * @param {{ includeHosting?: boolean }} [options]
 * @returns {Array<{ key: string, header: string, cellClassName?: string, render: Function }>}
 */
export function merchantListColumns({ includeHosting = false } = {}) {
  const columns = [
    {
      key: 'business',
      header: 'Business',
      render: (row) => (
        <p className="text-sm font-medium text-gray-900">{row.businessName}</p>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => {
        const badge = merchantAccountStatusBadge(resolveMerchantAccountStatus(row));
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
            {badge.label}
          </span>
        );
      }
    }
  ];

  if (includeHosting) {
    columns.push({
      key: HOSTING_COLUMN_KEY,
      header: 'Hosting',
      render: (row) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            row.hostingTier === 'paid'
              ? 'bg-indigo-50 text-indigo-700'
              : row.hostingTier
                ? 'bg-gray-100 text-gray-600'
                : 'bg-gray-50 text-gray-400'
          }`}
        >
          {hostingTierLabel(row.hostingTier)}
        </span>
      )
    });
  }

  columns.push(
    {
      key: 'users',
      header: 'Users',
      cellClassName: `${ADMIN_LIST_CHROME.td} text-sm text-gray-600`,
      render: (row) => row.userCount || 0
    },
    {
      key: 'created',
      header: 'Created',
      cellClassName: `${ADMIN_LIST_CHROME.td} text-sm text-gray-400`,
      render: (row) => formatAdminListDate(row.createdAt)
    }
  );

  return columns;
}

/**
 * @returns {ReturnType<typeof salesOrgListColumns>}
 */
export function cpSalesOrgColumns() {
  return salesOrgListColumns();
}

/**
 * @returns {ReturnType<typeof salesOrgListColumns>}
 */
export function lynxPaySalesOrgColumns() {
  return salesOrgListColumns({
    includeFeatures: true,
    showContactEmail: true,
    nameHeader: 'Tenant'
  });
}

/**
 * @returns {ReturnType<typeof merchantListColumns>}
 */
export function cpMerchantColumns() {
  return merchantListColumns({ includeHosting: false });
}

/**
 * LynxPay Sales Org → Merchants only. Under-CP uses cpMerchantColumns.
 * @returns {ReturnType<typeof merchantListColumns>}
 */
export function lynxPayMerchantColumns() {
  return merchantListColumns({ includeHosting: true });
}
